import React from "react";
import { Box, ButtonBase, Stack, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

/** One line per batch: "100 patches · 10 × 10". */
export function batchCaption(batch) {
    if (!batch) return "";
    const grid = Array.isArray(batch.grid_size) ? `${batch.grid_size[0]} × ${batch.grid_size[1]}` : null;
    const patches = `${batch.count} patch${batch.count === 1 ? "" : "es"}`;
    return grid ? `${patches} · ${grid}` : patches;
}

/** "Batch 2 · Repetition 1" — the plate (= one marking file, one photo per mode). */
export function plateLabel(batch, repetition) {
    return `Batch ${batch + 1} · Repetition ${repetition + 1}`;
}

const STATE_ICON = {
    complete: <CheckCircleIcon fontSize="small" color="success" />,
    partial: <ErrorOutlineIcon fontSize="small" color="warning" />,
    empty: <RadioButtonUncheckedIcon fontSize="small" sx={{ color: "text.disabled" }} />,
};

/**
 * The repeatability plates as a batch × repetition matrix of selectable
 * tiles. The palette is marked in batches of at most one camera field
 * (10 × 10 patches); every batch is marked once per repetition, and each
 * of those plates is one marking file and one photo per capture mode.
 *
 * `stateFor(batch, repetition)` (optional) returns `{ state, hint }` with
 * state ∈ complete | partial | empty, shown as an icon on the tile.
 */
export default function PlateMatrix({
    batches,
    repetitions,
    selected,
    onSelect,
    stateFor,
    disabled = false,
    ariaLabel = "Plates",
    /** Prefix of the tiles' data-testid (two matrices can share a page). */
    tileTestId = "plate-tile",
}) {
    const reps = Array.from({ length: Math.max(1, repetitions || 1) }, (_, i) => i);
    const rows = (batches || []).slice().sort((a, b) => a.batch - b.batch);
    if (rows.length === 0) return null;
    const isSelected = (batch, repetition) =>
        selected != null && selected.batch === batch && selected.repetition === repetition;

    return (
        <Box
            role="grid"
            aria-label={ariaLabel}
            sx={{
                display: "grid",
                gridTemplateColumns: `minmax(150px, max-content) repeat(${reps.length}, minmax(112px, 1fr))`,
                gap: 1,
                alignItems: "stretch",
                width: "100%",
                maxWidth: 150 + reps.length * 180,
            }}
        >
            <Box role="columnheader" />
            {reps.map((repetition) => (
                <Typography
                    key={`head-${repetition}`}
                    role="columnheader"
                    variant="caption"
                    color="text.secondary"
                    sx={{ px: 1, fontWeight: 600 }}
                >
                    {`Repetition ${repetition + 1}`}
                </Typography>
            ))}
            {rows.map((row) => (
                <React.Fragment key={row.batch}>
                    <Box role="rowheader" sx={{ px: 1, py: 0.75, minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={700}>{`Batch ${row.batch + 1}`}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>{batchCaption(row)}</Typography>
                    </Box>
                    {reps.map((repetition) => {
                        const status = stateFor ? stateFor(row.batch, repetition) : null;
                        const active = isSelected(row.batch, repetition);
                        const tile = (
                            <ButtonBase
                                role="gridcell"
                                aria-label={plateLabel(row.batch, repetition)}
                                aria-selected={active}
                                data-testid={`${tileTestId}-${row.batch}-${repetition}`}
                                data-state={status?.state ?? "none"}
                                disabled={disabled}
                                onClick={() => onSelect?.(row.batch, repetition)}
                                sx={{
                                    width: "100%",
                                    minHeight: 48,
                                    px: 1.25,
                                    py: 0.75,
                                    borderRadius: 2,
                                    border: "1px solid",
                                    borderColor: active ? "primary.main" : "divider",
                                    boxShadow: active ? (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}` : "none",
                                    bgcolor: active ? (theme) => alpha(theme.palette.primary.main, 0.08) : "background.paper",
                                    justifyContent: "space-between",
                                    textAlign: "left",
                                    transition: "border-color 0.15s ease, background-color 0.15s ease",
                                    "&:hover": disabled ? {} : { borderColor: "primary.main" },
                                }}
                            >
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
                                    <Typography variant="body2" fontWeight={active ? 700 : 500} sx={{ flex: 1 }}>
                                        {`Rep. ${repetition + 1}`}
                                    </Typography>
                                    {status ? STATE_ICON[status.state] ?? STATE_ICON.empty : null}
                                </Stack>
                            </ButtonBase>
                        );
                        return status?.hint ? (
                            <Tooltip key={`${row.batch}-${repetition}`} title={status.hint} placement="top">
                                <Box>{tile}</Box>
                            </Tooltip>
                        ) : (
                            <Box key={`${row.batch}-${repetition}`}>{tile}</Box>
                        );
                    })}
                </React.Fragment>
            ))}
        </Box>
    );
}
